<div class="tenant-list-container">
  <!-- Page Header -->
  <div class="page-header">
    <div class="header-content">
      <div class="breadcrumb">
        <mat-icon>home</mat-icon>
        <span>Dashboard</span>
        <mat-icon>chevron_right</mat-icon>
        <span>Tenants</span>
      </div>
      <h1>Tenant Management</h1>
      <p class="header-subtitle">Manage your organization tenants and their configurations</p>
    </div>
    <button mat-raised-button class="btn-primary btn-with-icon" (click)="openCreateDialog()">
      <mat-icon>add</mat-icon>
      <span>Add Tenant</span>
    </button>
  </div>

  <!-- Search and Filters Section -->
  <div class="search-section">
    <div class="search-controls">
      <mat-form-field appearance="outline" class="search-field">
        <mat-label>Search tenants</mat-label>
        <input matInput 
               [(ngModel)]="searchTerm" 
               (input)="filterTenants()"
               placeholder="Search by name, email, or location...">
        <mat-icon matPrefix>search</mat-icon>
        @if (searchTerm) {
          <button matSuffix mat-icon-button (click)="clearSearch()">
            <mat-icon>close</mat-icon>
          </button>
        }
      </mat-form-field>
      
      <div class="filter-stats">
        <span class="stats-badge">
          <mat-icon>business</mat-icon>
          <strong>{{ filteredTenants.length }}</strong> Tenant{{ filteredTenants.length !== 1 ? 's' : '' }}
        </span>
        <span class="stats-badge active">
          <mat-icon>check_circle</mat-icon>
          <strong>{{ getActiveTenantCount() }}</strong> Active
        </span>
        <span class="stats-badge inactive">
          <mat-icon>cancel</mat-icon>
          <strong>{{ getInactiveTenantCount() }}</strong> Inactive
        </span>
      </div>
    </div>
  </div>

  <!-- Tenant Grid -->
  <div class="content-section">
    @if (filteredTenants.length > 0) {
      <div class="tenant-grid">
        @for (tenant of paginatedTenants; track tenant.id) {
          <mat-card class="tenant-card" [class.inactive-card]="!tenant.isActive">
            <!-- Card Header -->
            <div class="card-header">
              <div class="tenant-avatar-wrapper">
                <div class="tenant-avatar" [class.inactive-avatar]="!tenant.isActive">
                  <span class="avatar-text">{{ tenant.name.substring(0, 2).toUpperCase() }}</span>
                </div>
                <div class="status-indicator" [class.active]="tenant.isActive" [class.inactive]="!tenant.isActive">
                  <mat-icon>{{ tenant.isActive ? 'check_circle' : 'cancel' }}</mat-icon>
                </div>
              </div>
            </div>

            <!-- Card Content -->
            <div class="card-content" (click)="openViewDialog(tenant)">
              <div class="tenant-title">
                <h3 class="tenant-name">{{ tenant.name }}</h3>
                <span class="tenant-slug">{{ tenant.slug }}</span>
              </div>

              @if (tenant.description) {
                <p class="tenant-description">{{ tenant.description }}</p>
              } @else {
                <p class="tenant-description no-description">No description provided</p>
              }

              <div class="tenant-details">
                @if (tenant.contactEmail) {
                  <div class="detail-item">
                    <mat-icon>email</mat-icon>
                    <span class="detail-text">{{ tenant.contactEmail }}</span>
                  </div>
                }
                @if (tenant.contactPhone) {
                  <div class="detail-item">
                    <mat-icon>phone</mat-icon>
                    <span class="detail-text">{{ tenant.contactPhone }}</span>
                  </div>
                }
                @if (tenant.city || tenant.country) {
                  <div class="detail-item">
                    <mat-icon>location_on</mat-icon>
                    <span class="detail-text">
                      @if (tenant.city && tenant.country) {
                        {{ tenant.city }}, {{ tenant.country }}
                      } @else if (tenant.city) {
                        {{ tenant.city }}
                      } @else {
                        {{ tenant.country }}
                      }
                    </span>
                  </div>
                }
                @if (tenant.website) {
                  <div class="detail-item">
                    <mat-icon>language</mat-icon>
                    <span class="detail-text">{{ tenant.website }}</span>
                  </div>
                }
              </div>
            </div>

            <!-- Card Actions -->
            <div class="card-actions">
              <button mat-button class="action-btn view-btn" (click)="openViewDialog(tenant)">
                <mat-icon>visibility</mat-icon>
                <span>View</span>
              </button>
              <button mat-icon-button class="action-icon-btn edit-btn" (click)="openEditDialog(tenant)" matTooltip="Edit">
                <mat-icon>edit</mat-icon>
              </button>
              <button mat-icon-button class="action-icon-btn modules-btn" (click)="openModuleAssignmentDialog(tenant)" matTooltip="Modules">
                <mat-icon>apps</mat-icon>
              </button>
              <button mat-icon-button class="action-icon-btn password-btn" (click)="openSetPasswordDialog(tenant)" matTooltip="Password">
                <mat-icon>lock</mat-icon>
              </button>
              <button mat-icon-button class="action-icon-btn delete-btn" (click)="deleteTenant(tenant)" matTooltip="Delete">
                <mat-icon>delete</mat-icon>
              </button>
            </div>
          </mat-card>
        }
      </div>

      <!-- Pagination -->
      <div class="pagination-section">
        <app-pagination
          [totalItems]="totalItems"
          [pageSize]="pageSize"
          [currentPage]="currentPage"
          [pageSizeOptions]="pageSizeOptions"
          [showPageSizeSelector]="true"
          [showPageInfo]="true"
          [showFirstLastButtons]="true"
          (pageChange)="onPageChange($event)">
        </app-pagination>
      </div>
    } @else {
      <!-- Empty State -->
      <div class="empty-state">
        <div class="empty-icon-wrapper">
          <mat-icon class="empty-icon">business</mat-icon>
        </div>
        <h3 class="empty-title">No Tenants Found</h3>
        @if (searchTerm) {
          <p class="empty-message">No tenants match your search criteria. Try adjusting your search.</p>
          <button mat-raised-button class="btn-secondary" (click)="clearSearch()">
            <mat-icon>clear</mat-icon>
            Clear Search
          </button>
        } @else {
          <p class="empty-message">Get started by creating your first tenant organization</p>
          <button mat-raised-button class="btn-primary btn-with-icon" (click)="openCreateDialog()">
            <mat-icon>add</mat-icon>
            <span>Add First Tenant</span>
          </button>
        }
      </div>
    }
  </div>
</div>
